"use client";

import { useEffect, useRef, type RefObject } from "react";

type VideoElement = HTMLVideoElement;

/** Attach enforcement listeners to a single video element. Returns a cleanup function. */
function enforceVideo(video: VideoElement): () => void {
  let active = true;

  const enforce = () => {
    if (!active) return;
    if (!video.muted) video.muted = true;
    if (video.volume !== 0) video.volume = 0;
  };

  enforce();

  const observer = new MutationObserver(enforce);
  observer.observe(video, { attributes: true, attributeFilter: ["muted"] });

  video.addEventListener("volumechange", enforce);
  video.addEventListener("play", enforce);
  video.addEventListener("playing", enforce);

  return () => {
    active = false;
    observer.disconnect();
    video.removeEventListener("volumechange", enforce);
    video.removeEventListener("play", enforce);
    video.removeEventListener("playing", enforce);
  };
}

/**
 * Enforces `muted = true` and `volume = 0` on a single video element via ref.
 * Multi-layer defense:
 *  1. MutationObserver on the `muted` HTML attribute
 *  2. `volumechange` event – catches browser property changes that skip the attribute
 *  3. `play` / `playing` events – re-mute if playback is triggered externally (e.g. Cmd+A focus)
 *  4. requestAnimationFrame polling – last-resort guard (~500ms)
 */
export function useForceMuted(videoRef: RefObject<VideoElement | null>) {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    cleanupRef.current?.();
    cleanupRef.current = enforceVideo(el);

    // Polling fallback for this single element
    const poll = () => {
      if (!el.isConnected) return;
      if (!el.muted) el.muted = true;
      if (el.volume !== 0) el.volume = 0;
    };
    const interval = window.setInterval(poll, 500);

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      clearInterval(interval);
    };
  }, [videoRef]);
}

/**
 * Enforces muted state on ALL `<video>` elements inside a container.
 * Uses MutationObserver to catch dynamically added videos (e.g. detail gallery).
 * Multi-layer defense identical to `useForceMuted`, applied per-element.
 */
export function useForceMutedAll(containerRef: RefObject<HTMLElement | null>) {
  const cleanupsRef = useRef<Map<VideoElement, () => void>>(new Map());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const cleanups = cleanupsRef.current;

    const attachTo = (video: VideoElement) => {
      if (cleanups.has(video)) return;
      cleanups.set(video, enforceVideo(video));
    };

    const detachFrom = (video: VideoElement) => {
      const fn = cleanups.get(video);
      if (fn) {
        fn();
        cleanups.delete(video);
      }
    };

    // Attach to existing videos
    container.querySelectorAll<VideoElement>("video").forEach(attachTo);

    // Watch for dynamically added/removed videos
    const domObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (node instanceof HTMLVideoElement) attachTo(node);
          if (node instanceof HTMLElement) {
            node.querySelectorAll<VideoElement>("video").forEach(attachTo);
          }
        }
        for (const node of Array.from(m.removedNodes)) {
          if (node instanceof HTMLVideoElement) detachFrom(node);
          if (node instanceof HTMLElement) {
            node.querySelectorAll<VideoElement>("video").forEach(detachFrom);
          }
        }
      }
    });
    domObserver.observe(container, { childList: true, subtree: true });

    // Polling fallback for all tracked videos
    const interval = window.setInterval(() => {
      for (const video of cleanups.keys()) {
        if (!video.isConnected) {
          detachFrom(video);
          continue;
        }
        if (!video.muted) video.muted = true;
        if (video.volume !== 0) video.volume = 0;
      }
    }, 500);

    return () => {
      domObserver.disconnect();
      clearInterval(interval);
      for (const fn of cleanups.values()) fn();
      cleanups.clear();
    };
  }, [containerRef]);
}
